/**
 * Design State Model
 *
 * Every design in stencil.cloud is a serializable JSON document.
 * Templates are pre-populated instances of this schema.
 * Fabric.js canvas objects sync bidirectionally with this state.
 */

export function createDesignState(overrides = {}) {
  return {
    version: 1,
    template: 'generic-event',
    size: 'letter',
    scheme: {
      name: 'dark',
      bg: '#1a1a1a',
      primary: '#ffffff',
      secondary: '#cccccc',
      accent: '#ff4444',
      date: '#ff4444',
      detail: '#999999',
      lineArt: '#333333',
      footer: '#666666',
      isDark: true,
    },
    objects: [],
    overlays: {
      lineArt: {
        type: 'none',
        seed: Math.floor(Math.random() * 100000),
        opacity: 0.3,
        density: 'medium',
      },
      lineArt2: {
        type: 'none',
        seed: 137,
        opacity: 0.2,
        density: 30,
      },
      grain: {
        enabled: false,
        density: 0.4,
        opacity: 0.15,
      },
    },
    eventDateISO: '', // YYYY-MM-DD — used by scheduler
    watermark: true,  // false only for paid tier
    ...overrides,
  };
}

/**
 * Template sizes — organized by category.
 * Every template can export to any of these sizes.
 * The layout engine reflows object positions proportionally.
 */

// Print formats (300 DPI equivalent)
export const PRINT_SIZES = {
  'letter':  { width: 2550, height: 3300, label: 'Letter Flyer (8.5×11")', category: 'print' },
  'tabloid': { width: 3300, height: 5100, label: 'Tabloid Poster (11×17")', category: 'print' },
};

// Social media + digital
export const SOCIAL_SIZES = {
  'social-vertical': { width: 1080, height: 1350, label: 'Social Vertical (4:5)', category: 'social' },
  'story':           { width: 1080, height: 1920, label: 'Story / Reel (9:16)', category: 'social' },
  'event-banner':    { width: 1500, height: 500,  label: 'Event Banner (3:1)', category: 'social' },
  'pinterest':       { width: 1000, height: 1500, label: 'Pinterest Pin (2:3)', category: 'social' },
  'yt-thumbnail':    { width: 1280, height: 720,  label: 'YouTube Thumbnail (16:9)', category: 'social' },
  'threads':         { width: 1440, height: 1920, label: 'Threads Post (3:4)', category: 'social' },
  'bandcamp':        { width: 2800, height: 2800, label: 'Bandcamp Album (1:1)', category: 'social' },
};

// Combined lookup
export const TEMPLATE_SIZES = {
  ...PRINT_SIZES,
  ...SOCIAL_SIZES,
};

/**
 * Default export size when a user first opens a template.
 * Letter is the most universally useful starting point.
 */
export const DEFAULT_SIZE = 'letter';

/**
 * Serialize Fabric.js canvas objects into our state format.
 */
export function fabricToState(fabricCanvas) {
  const objects = fabricCanvas.getObjects()
    .filter(obj => obj.data?.id) // only serialize objects with our custom ID
    .map(obj => ({
      type: obj.data?.stencilType || obj.type,
      id: obj.data.id,
      x: Math.round(obj.left),
      y: Math.round(obj.top),
      width: Math.round(obj.width * (obj.scaleX || 1)),
      height: Math.round(obj.height * (obj.scaleY || 1)),
      angle: obj.angle || 0,
      locked: obj.lockMovementX && obj.lockMovementY,
      ...serializeByType(obj),
    }));

  return objects;
}

function serializeByType(obj) {
  if (obj.type === 'textbox' || obj.type === 'i-text') {
    return {
      content: obj.text,
      fontFamily: obj.fontFamily,
      fontSize: obj.fontSize,
      fontWeight: obj.fontWeight || 'normal',
      fontStyle: obj.fontStyle || 'normal',
      underline: obj.underline || false,
      fill: obj.fill,
      textAlign: obj.textAlign || 'left',
      role: obj.data?.role,
      userCreated: obj.data?.userCreated || false,
      fxLocked: obj.data?.fxLocked || false,
      textBg: obj.data?.textBg || null,
      sliceEffect: obj.data?.sliceEffect || null,
    };
  }
  if (obj.data?.stencilType === 'qr') {
    return {
      url: obj.data.url,
      size: obj.data.size,
      style: obj.data.style || 'square',
      fgColor: obj.data.fgColor || '#000000',
      bgColor: obj.data.bgColor || '#ffffff',
    };
  }
  return {};
}
